import React from "react";
import "./legal.css";
import { AlertTriangle } from "lucide-react";

/**
 * Standalone, unauthenticated page at /privacy. See TermsOfService.js for
 * the sibling page and the rationale - this product stores patient
 * personal data (names, contact info, dates of birth, treatment notes)
 * and had no privacy policy anywhere before this.
 */
const Placeholder = ({ children }) => <span className="legal-placeholder">{children}</span>;

export default function PrivacyPolicy() {
  return (
    <div className="legal-page" data-testid="privacy-policy-page">
      <div className="legal-shell">
        <div className="legal-topbar">
          <a href="/" className="legal-brand">MedSpaBookings OS</a>
          <nav>
            <a href="/terms">Terms of Service</a>
            <a href="/privacy">Privacy Policy</a>
          </nav>
        </div>

        <div className="legal-draft-banner" data-testid="privacy-draft-banner">
          <AlertTriangle size={20} />
          <div>
            <strong>DRAFT — pending legal review</strong>
            <p>
              This Privacy Policy is a placeholder drafted to cover the standard sections a SaaS handling
              personal and patient data needs. It has not been reviewed by a licensed attorney or privacy
              professional and must not be treated as a binding disclosure until it is — including for
              healthcare-adjacent obligations such as HIPAA or equivalent regimes. Bracketed text such as{" "}
              <Placeholder>[COMPANY LEGAL NAME]</Placeholder> marks values that still need to be filled in.
            </p>
          </div>
        </div>

        <article className="legal-doc glass-panel" data-testid="privacy-policy-content">
          <h1>Privacy Policy</h1>
          <p className="legal-meta">Draft version 0.1 &middot; Last updated: <Placeholder>[DATE]</Placeholder></p>

          <h2>1. Scope</h2>
          <p>
            This Privacy Policy explains how <Placeholder>[COMPANY LEGAL NAME]</Placeholder> ("we", "us", "our")
            collects, uses, discloses, and protects personal data through MedSpaBookings OS (the "Service"). It
            covers two audiences: (a) med-spa businesses and their staff who use the Service ("Customers"), and
            (b) patients of those businesses whose information is entered into the Service, including through
            the public self-serve booking widget. This policy works alongside our{" "}
            <a href="/terms">Terms of Service</a>.
          </p>

          <h2>2. Information We Collect</h2>
          <h3>Account and business information</h3>
          <p>
            When a Customer signs up, we collect business/clinic details, staff names, email addresses, and
            phone numbers, and — through our payment processor, Stripe — billing and subscription information.
            We do not store full payment card numbers ourselves; Stripe handles and stores that data.
          </p>
          <h3>Patient information</h3>
          <p>
            Customers and their patients may submit patient personal data into the Service, including but not
            limited to: name, contact information (email, phone), date of birth, appointment history, treatment
            or service notes, and provider assignments. This data is entered either by Customer staff or
            directly by a patient through the public booking widget.
          </p>
          <h3>Usage and technical data</h3>
          <p>
            We automatically collect limited technical data needed to operate and secure the Service, such as
            login timestamps, IP address, and basic device/browser information for security and abuse
            prevention (e.g. rate limiting on activation and login endpoints).
          </p>

          <h2>3. How We Use Information</h2>
          <ul>
            <li>To provide, maintain, and secure the Service (accounts, scheduling, billing, communications).</li>
            <li>To send transactional emails (e.g. account activation, appointment confirmations) via SendGrid and SMS/voice notifications via Twilio, where enabled by the Customer.</li>
            <li>To process payments and manage subscriptions via Stripe.</li>
            <li>To detect, prevent, and respond to fraud, abuse, and security incidents.</li>
            <li>To comply with legal obligations and enforce our Terms of Service.</li>
            <li>To improve the Service, using aggregated or de-identified data where practical.</li>
          </ul>
          <p>
            We do not sell personal data, and we do not use patient personal data for advertising.
          </p>

          <h2>4. Data Retention</h2>
          <p>
            We retain personal data for as long as the associated Customer account is active, and for a
            reasonable period afterward to meet legal, accounting, security, or dispute-resolution needs, per
            our retention schedule (to be finalized — see <Placeholder>[DATA RETENTION SCHEDULE]</Placeholder>).
            Customers may request deletion of their account and associated patient data as described in Section
            7 below, subject to records we are legally required to retain.
          </p>

          <h2>5. Third-Party Service Providers</h2>
          <p>
            We share personal data only with service providers who process it on our behalf, under contract, to
            operate the Service. Current categories of providers include:
          </p>
          <ul>
            <li><strong>Stripe</strong> — payment processing and subscription billing.</li>
            <li><strong>SendGrid</strong> — delivery of transactional and account-related email.</li>
            <li><strong>Twilio</strong> — SMS and voice communications for appointment/account notifications.</li>
            <li><strong>MongoDB Atlas</strong> — hosted database infrastructure where Service data, including patient data, is stored.</li>
          </ul>
          <p>
            We do not otherwise sell, rent, or share personal data with third parties for their own marketing
            purposes. We may disclose data where required by law, to protect our rights or the safety of others,
            or in connection with a merger, acquisition, or sale of assets, subject to standard confidentiality
            protections.
          </p>

          <h2>6. Data Security</h2>
          <p>
            We use technical and organizational measures intended to protect personal data, including encrypted
            transport, hashed credentials, rate limiting on authentication-sensitive endpoints, and
            role-restricted access to patient data within the Service. No method of transmission or storage is
            completely secure, and we cannot guarantee absolute security.
          </p>

          <h2>7. Your Rights and Choices</h2>
          <p>
            Depending on your jurisdiction, you may have rights to access, correct, export, or request deletion
            of your personal data, and to object to or restrict certain processing. Patients whose information
            is held by a Customer should generally direct such requests to that med-spa business in the first
            instance, as they are typically the data controller; Customers and patients may also contact us
            directly at the address below and we will route the request appropriately.
          </p>

          <h2>8. Children's Data</h2>
          <p>
            The Service is intended for use by adult patients and med-spa businesses. We do not knowingly
            collect personal data from children. Where a Customer's patient population includes minors, the
            Customer is responsible for obtaining any parental/guardian consent required by applicable law
            before submitting that minor's data to the Service.
          </p>

          <h2>9. International Data Transfers</h2>
          <p>
            Personal data may be processed and stored in <Placeholder>[HOSTING REGION(S)]</Placeholder> by us and
            our service providers listed above. Where data is transferred across borders, we rely on appropriate
            safeguards required by applicable law (to be finalized — see{" "}
            <Placeholder>[TRANSFER MECHANISM, e.g. SCCs]</Placeholder>).
          </p>

          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Material changes will be reflected by an
            updated "Last updated" date on this page and, where required by law or where the change is
            significant, additional notice will be provided.
          </p>

          <h2>11. Contact Us</h2>
          <p>
            For privacy questions or to exercise your rights, contact{" "}
            <a href="mailto:support@medspabookings.co">support@medspabookings.co</a> or{" "}
            <Placeholder>[PRIVACY/COMPLIANCE CONTACT EMAIL]</Placeholder>, or by mail to{" "}
            <Placeholder>[BUSINESS ADDRESS]</Placeholder>.
          </p>
        </article>

        <p className="legal-footer">
          <a href="/terms">Terms of Service</a> &middot; <a href="/">Back to MedSpaBookings OS</a>
        </p>
      </div>
    </div>
  );
}
